import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BoltIcon,
  ChartBarIcon,
  CpuChipIcon,
  PhoneIcon,
  UserIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '../../components/UI/Card';
import { adminService } from '../../services/adminService';
import Button from '../../components/UI/Button';
import { toast } from 'react-hot-toast';
import { getSseAccessToken } from '../../utils/sseAuthToken';
import LiveCallsSection from '../../components/LiveCalls/LiveCallsSection';

/**
 * Bot Monitor — live view of AI calling-bot conversations.
 *
 * Dedicated to bot calls ONLY. It connects to the isolated SSE stream
 * `/api/bot-monitor/stream` (one connection for live call events + analytics),
 * so it never interferes with the manual dialer, auto-dialer, or SMS.
 */

interface Turn {
  role: string; // 'assistant' | 'customer'
  content: string;
  at?: string;
}

interface BotCall {
  callControlId: string;
  campaignId?: string | null;
  campaignName?: string | null;
  leadId?: string | null;
  leadName?: string | null;
  leadPhone?: string | null;
  startedAt?: string;
  endedAt?: string;
  status: string; // connecting | active | ending | ended
  turns: Turn[];
  lastText?: string | null;
  endReason?: string;
  /** True when a real person spoke (not VM / IVR-only). */
  humanConnected?: boolean;
  engine?: 'telnyx_assistant' | 'conversation_relay' | 'vapi_direct' | 'self_hosted';
}

/** Initial visible rows; Load more grows this. */
const CALL_LIST_PAGE_SIZE = 12;
/** Drop non-today ended cards so the list stays usable. Today's ended stay all day. */
const ENDED_CALL_RETENTION_MS = 3 * 60 * 1000;
/** Hard cap on ended cards kept in memory (newest kept). */
const MAX_ENDED_CALLS = 120;

function isSameLocalDay(iso?: string): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

type CallStatusTab = 'all' | 'connecting' | 'active' | 'connected' | 'ended';

const CALL_STATUS_TABS: Array<{ id: CallStatusTab; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'connecting', label: 'Connecting' },
  { id: 'active', label: 'Active' },
  { id: 'connected', label: 'Connected' },
  { id: 'ended', label: 'Ended' },
];

const HUMAN_TURN_ROLES = /^(user|customer|human|caller)$/i;

/** Mirrors backend iphoneCallScreening / botAutoDisposition VM phrases (Justina / carrier). */
const VOICEMAIL_PHRASE_RES: RegExp[] = [
  /\bvoicemail\b/i,
  /\bvoice\s*mail\b/i,
  /\bleave\s+(a\s+|your\s+)?message\b/i,
  /\bafter\s+the\s+(tone|beep)\b/i,
  /\bat\s+the\s+(tone|beep)\b/i,
  /\bat\s+the\s+sound\s+of\s+the\s+(tone|beep)\b/i,
  /\bunable\s+to\s+(take|answer)\s+(your\s+)?call\b/i,
  /\bis\s+not\s+available\b/i,
  /\bmailbox\s+is\s+full\b/i,
  /\bplease\s+leave\s+your\s+message\b/i,
  /\bplease\s+record\s+(your\s+)?message\b/i,
  /\b(call|your\s+call)\s+has\s+been\s+forwarded\b/i,
  /\bforwarded\s+to\s+(an?\s+)?(voice\s*)?mail\b/i,
  /\bthe\s+person\s+you(?:'re|\s+are)\s+trying\s+to\s+reach\b/i,
  /\bfinished\s+recording\b/i,
  /\bwhen\s+you\s+have\s+finished\s+recording\b/i,
  /\byou\s+may\s+hang\s+up\b/i,
  /\banswering\s+machine\b/i,
];

const SCREENING_PHRASE_RES: RegExp[] = [
  /please\s+state\s+your\s+name/i,
  /state\s+your\s+name\s+and\s+(the\s+)?reason/i,
  /name\s+and\s+(the\s+)?reason\s+for\s+(your\s+)?call/i,
  /reason\s+for\s+(your\s+)?call(ing)?/i,
  /record\s+your\s+name\s+and\s+reason/i,
  /if\s+you\s+record\s+your\s+name/i,
  /see\s+if\s+this\s+person\s+is\s+available/i,
  /please\s+stay\s+on\s+the\s+line/i,
  /\bstay\s+on\s+the\s+line\b/i,
];

const OPERATOR_IVR_RE =
  /\b(press\s*(?:[0-9]|one|two|three|four|five|six|seven|eight|nine|zero)|for\s+(?:an?\s+)?operator|auto[\s-]?attendant|dial\s+by\s+name|main\s+menu|company\s+directory|enter\s+an?\s+extension)\b/i;

const HUMAN_PICKUP_RE =
  /\b(hello|hi|yeah|yes|speaking|this\s+is|who'?s\s+(this|calling)|who\s+is\s+(this|calling)|can\s+i\s+help|what'?s\s+(this|up)|go\s+ahead)\b/i;

function isScreeningOrVmOrIvrLine(content: string): boolean {
  const t = String(content || '').replace(/\s+/g, ' ').trim();
  if (!t) return false;
  return (
    SCREENING_PHRASE_RES.some((re) => re.test(t)) ||
    VOICEMAIL_PHRASE_RES.some((re) => re.test(t)) ||
    OPERATOR_IVR_RE.test(t)
  );
}

function turnsLookLikeHumanSpeech(turns: Turn[] | undefined): boolean {
  const customer = (turns || []).filter(
    (t) => HUMAN_TURN_ROLES.test(String(t.role || '')) && String(t.content || '').trim().length > 0
  );
  if (customer.length === 0) return false;
  const machineOnly = customer.every((t) => isScreeningOrVmOrIvrLine(t.content));
  if (machineOnly) return false;
  const hasScreeningOrVm = customer.some((t) => isScreeningOrVmOrIvrLine(t.content));
  const hasHuman = customer.some((t) => {
    const c = String(t.content || '').trim();
    return !isScreeningOrVmOrIvrLine(c) && HUMAN_PICKUP_RE.test(c);
  });
  if (hasScreeningOrVm && !hasHuman) return false;
  return hasHuman || customer.some((t) => !isScreeningOrVmOrIvrLine(t.content));
}

/** Real human talk — excludes voicemail / operator-only outcomes (live + ended). */
function isHumanConnectedCall(c: BotCall): boolean {
  const reason = String(c.endReason || '').toLowerCase();
  if (
    reason.includes('voicemail') ||
    reason.includes('operator') ||
    reason.includes('no answer') ||
    reason.includes('predictive cancel')
  ) {
    return false;
  }
  // Prefer live transcript heuristics so Active tab never badges VM as Human.
  if ((c.turns || []).length > 0) {
    return turnsLookLikeHumanSpeech(c.turns);
  }
  if (c.humanConnected === true) return true;
  if (c.humanConnected === false && c.status === 'ended') return false;
  return false;
}

function matchesCallStatusTab(call: BotCall, tab: CallStatusTab): boolean {
  if (tab === 'all') return true;
  if (tab === 'connected') return isHumanConnectedCall(call);
  if (tab === 'active') return call.status === 'active' || call.status === 'ending';
  return call.status === tab;
}

function engineLabel(engine?: BotCall['engine']): string {
  if (engine === 'telnyx_assistant') return 'Telnyx AI';
  if (engine === 'conversation_relay') return 'Custom OpenAI';
  if (engine === 'vapi_direct') return 'VAPI Direct';
  return 'Bot';
}

function engineBadgeClass(engine?: BotCall['engine']): string {
  if (engine === 'telnyx_assistant') {
    return 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300';
  }
  if (engine === 'conversation_relay') {
    return 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300';
  }
  if (engine === 'vapi_direct') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
  }
  return 'bg-muted text-muted-foreground';
}

function buildBotMonitorStreamUrl(token: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
  const fullUrl = baseUrl.startsWith('http')
    ? `${baseUrl}/bot-monitor/stream`
    : `${window.location.origin}${baseUrl}/bot-monitor/stream`;
  const url = new URL(fullUrl);
  url.searchParams.set('token', token);
  return url.toString();
}

const statusStyles: Record<string, string> = {
  connecting: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  ending: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  ended: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

interface BotCampaignCapacityMetrics {
  dialingMode: 'legacy' | 'capacity';
  targetActiveAi: number;
  activeAiConversations: number;
  pipelineCalls: number;
  pipelineCap: number;
  pipelineRoom: number;
  answerRate: number;
  recommendedWaveSize: number;
  avgTurnLatencyMs: number;
  latencyElevated: boolean;
  avgSessionAgeSec: number;
}

interface BotCampaignPacing {
  dialRatio: number;
  targetActiveCalls: number;
  activeCalls: number;
  deficit: number;
  availableAgents: number;
  isPaused: boolean;
  pausedUntil: string | null;
  fillPercent: number;
  health: 'normal' | 'slowed' | 'paused';
  predictiveCancelledRate: number;
}

interface BotCampaignPacingCard {
  id: string;
  name: string;
  status: string;
  dialRatio: number;
  pacing: BotCampaignPacing;
  capacity?: BotCampaignCapacityMetrics;
}

interface BotMonitorAgent {
  id: string;
  aiName: string;
  campaignId: string;
  campaignName: string;
  conversationStyleId: string | null;
  maxCalls: number;
  activeCalls: number;
  bridgedCalls: number;
  ringingCalls: number;
  callStatus: 'in-call' | 'ringing' | 'idle';
  dailyCounters: {
    totalCallsDialed: number;
    answeredCalls: number;
    interested: number;
    followUps: number;
    appIn: number;
    appOut: number;
  };
  avgWaitBetweenCallsSeconds: number;
  secondsSinceLastContact: number;
  currentIdleWaitSeconds: number;
}

interface BotActivityEvent {
  id: string;
  at: string;
  level: 'info' | 'success' | 'warning' | 'error';
  type: string;
  message: string;
  campaignId?: string;
  campaignName?: string;
  leadId?: string;
  leadName?: string;
  leadPhone?: string;
  callControlId?: string;
  meta?: Record<string, unknown>;
}

const BOT_ACTIVITY_LEVEL_STYLES: Record<BotActivityEvent['level'], string> = {
  info: 'text-foreground',
  success: 'text-green-700 dark:text-green-400',
  warning: 'text-amber-700 dark:text-amber-400',
  error: 'text-red-700 dark:text-red-400',
};

const BOT_ACTIVITY_TYPE_LABELS: Record<string, string> = {
  dial_initiated: 'Dial',
  customer_ringing: 'Ring',
  customer_answered: 'Answered',
  call_ended: 'Ended',
  batch_scheduled: 'Schedule',
  dial_skipped: 'Skip',
};

interface DialWindowSnapshot {
  enabled: boolean;
  timezoneSource: string;
  activeZones: string[];
  etWaveZones: string[] | null;
  nowEt: string;
  eligibleLeadsInWindow: number;
  eligibleLeadsTotal: number;
  idleReason: string;
  idleReasonLabel: string;
}

interface Analytics {
  activeNow: number;
  /** In-memory live call ids — used to clear stuck Active cards if call_ended was missed. */
  activeCallIds?: string[];
  /** Interruptions today: how many the caller answered vs how many hit the turn cap. */
  interruptions?: {
    opened: number;
    byKind: Record<string, number>;
    resolvedByAnswer: number;
    forceResolved: number;
    forceResolvedRate: number;
  };
  last24h: { calls: number };
  last7d: {
    calls: number;
    avgDurationSec: number;
    totalMinutes: number;
    actualCostUsd?: number;
    estimatedCostUsd: number;
    totalCostUsd?: number;
    chargedCalls?: number;
    avgCostPerMinuteUsd?: number;
    dispositions: { disposition: string; count: number }[];
  };
  leadStatusCounts?: Record<string, number>;
  breakdownStats?: {
    totalDialedToday: number;
    connectedToday: number;
    avgWaitToNextCallSeconds: number;
    connectedAvgDuration: number;
  };
  campaignPacing?: BotCampaignPacingCard[];
  agents?: BotMonitorAgent[];
  dialWindow?: DialWindowSnapshot;
}

/** Same buckets/colors as Auto Dial / Live Monitor Lead Status Breakdown. */
const LEAD_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  // Do not show rollup "Answered" here — it used to equal Hang Up when hangups
  // dominated (sum of dispositions including hangup), which confused the client.
  interested: { label: 'Interested', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
  not_interested: { label: 'Not Interested', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  follow_up: { label: 'Follow Up', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  no_answer: { label: 'No Answer', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
  busy: { label: 'Busy', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  unreachable: { label: 'Unreachable', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  do_not_call: { label: 'Do Not Call', color: 'bg-red-200 text-red-900 dark:bg-red-900/40 dark:text-red-300' },
  out_of_business: { label: 'Out of Business', color: 'bg-gray-200 text-gray-900 dark:bg-gray-800/40 dark:text-gray-300' },
  wrong_number: { label: 'Wrong Number', color: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400' },
  unqualified: { label: 'Unqualified', color: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400' },
  gatekeeper: { label: 'Gatekeeper', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400' },
  app_in: { label: 'App In', color: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400' },
  app_out: { label: 'App Out', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' },
  spanish: { label: 'Spanish', color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400' },
  hangup: { label: 'Hang Up', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  operator: { label: 'Operator', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400' },
  voicemail: { label: 'Voicemail', color: 'bg-orange-200 text-orange-900 dark:bg-orange-900/40 dark:text-orange-300' },
  cancelled: { label: 'Predictive Cancelled', color: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400' },
};

const PACING_HEALTH_STYLES: Record<
  BotCampaignPacing['health'],
  { label: string; className: string }
> = {
  normal: { label: 'Normal', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  slowed: { label: 'Slowed', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  paused: { label: 'Paused', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
};

function pcRateColor(pct: number): string {
  if (pct >= 10) return 'text-red-600 dark:text-red-400';
  if (pct >= 5) return 'text-amber-600 dark:text-amber-400';
  if (pct >= 3) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
}

function fmtDuration(sec: number): string {
  if (!sec || sec <= 0) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatWaitTime(sec?: number): string {
  const safe = Math.max(Math.floor(sec || 0), 0);
  if (safe >= 3600) return `${Math.floor(safe / 3600)}h ${Math.floor((safe % 3600) / 60)}m`;
  if (safe >= 60) return `${Math.floor(safe / 60)}m ${safe % 60}s`;
  return `${safe}s`;
}

function formatSeconds(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

const StatCard: React.FC<{ label: string; value: string; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <Card className="card-elevated">
    <CardContent className="p-4">
      <div className="text-2xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
      {hint && (
        <div className="text-[11px] text-muted-foreground/80 mt-0.5 truncate" title={hint}>
          {hint}
        </div>
      )}
    </CardContent>
  </Card>
);

const BotMonitorPage: React.FC = () => {
  const [calls, setCalls] = useState<Record<string, BotCall>>({});
  const [activityLog, setActivityLog] = useState<BotActivityEvent[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [visibleLimit, setVisibleLimit] = useState(CALL_LIST_PAGE_SIZE);
  const [statusTab, setStatusTab] = useState<CallStatusTab>('active');
  const [readiness, setReadiness] = useState<Awaited<
    ReturnType<typeof adminService.getTelnyxBotReadiness>
  > | null>(null);
  const [syncing, setSyncing] = useState(false);
  const esRef = useRef<EventSource | null>(null);
  const closedRef = useRef(false);
  const reconnectTimerRef = useRef<number | null>(null);

  // Single SSE stream: live call events + periodic analytics. Matches the app's
  // other monitors (human/auto-dial/SMS use SSE); reconnects with a fresh token.
  useEffect(() => {
    closedRef.current = false;

    const cleanup = () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      try {
        esRef.current?.close();
      } catch {
        /* ignore */
      }
      esRef.current = null;
    };

    const scheduleReconnect = () => {
      if (closedRef.current || reconnectTimerRef.current) return;
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        void connect();
      }, 3000);
    };

    const connect = async () => {
      cleanup();
      if (closedRef.current) return;

      const token = await getSseAccessToken();
      if (!token) {
        scheduleReconnect();
        return;
      }

      const es = new EventSource(buildBotMonitorStreamUrl(token));
      esRef.current = es;

      es.onopen = () => setConnected(true);

      es.onmessage = (ev) => {
        let msg: any;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (!msg || !msg.type) return;

        if (msg.type === 'analytics') {
          const data = (msg.data ?? null) as Analytics | null;
          setAnalytics(data);
          // Reconcile stuck Active cards: if server no longer has the session,
          // mark local card ended (missed call_ended / SSE gap).
          const liveIds = Array.isArray(data?.activeCallIds)
            ? new Set(data.activeCallIds.map(String))
            : null;
          if (liveIds) {
            const nowMs = Date.now();
            const nowIso = new Date(nowMs).toISOString();
            // Grace: analytics is async; a brand-new call_started can race an
            // in-flight analytics payload that predates the session.
            const RECONCILE_GRACE_MS = 20_000;
            setCalls((prev) => {
              let changed = false;
              const next = { ...prev };
              for (const [id, c] of Object.entries(prev)) {
                if (c.status === 'ended') continue;
                if (liveIds.has(id)) continue;
                const startedMs = Date.parse(c.startedAt || '');
                if (Number.isFinite(startedMs) && nowMs - startedMs < RECONCILE_GRACE_MS) {
                  continue;
                }
                next[id] = {
                  ...c,
                  status: 'ended',
                  endReason: c.endReason || 'reconciled',
                  endedAt: c.endedAt || nowIso,
                };
                changed = true;
              }
              return changed ? next : prev;
            });
          }
          return;
        }

        if (msg.type === 'activity_snapshot' && Array.isArray(msg.events)) {
          setActivityLog(msg.events);
          return;
        }

        if (msg.type === 'activity_event' && msg.event) {
          setActivityLog((prev) => [msg.event as BotActivityEvent, ...prev].slice(0, 100));
          return;
        }

        if (msg.type === 'snapshot' && Array.isArray(msg.calls)) {
          setCalls((prev) => {
            const next: Record<string, BotCall> = {};
            for (const c of msg.calls) {
              const existing = prev[c.callControlId];
              next[c.callControlId] = {
                callControlId: c.callControlId,
                campaignId: c.campaignId,
                campaignName: c.campaignName ?? null,
                leadId: c.leadId,
                leadName: c.leadName ?? null,
                leadPhone: c.leadPhone ?? null,
                startedAt: c.startedAt,
                endedAt: c.endedAt ?? existing?.endedAt,
                status: c.status || 'active',
                turns: Array.isArray(c.turns) && c.turns.length > 0
                  ? c.turns
                  : existing?.turns ?? [],
                lastText: c.lastText ?? existing?.lastText ?? null,
                endReason: c.endReason ?? existing?.endReason,
                humanConnected:
                  typeof c.humanConnected === 'boolean'
                    ? c.humanConnected
                    : existing?.humanConnected,
                engine: c.engine ?? existing?.engine,
              };
            }
            // Keep today's ended cards if snapshot briefly omits them (DB lag / race).
            for (const [id, c] of Object.entries(prev)) {
              if (next[id]) continue;
              if (c.status !== 'ended') continue;
              if (isSameLocalDay(c.endedAt) || isSameLocalDay(c.startedAt)) {
                next[id] = c;
              }
            }
            return next;
          });
          setSelectedId((prev) => prev || (msg.calls[0]?.callControlId ?? null));
          return;
        }

        if (msg.type === 'call_started') {
          const c = msg.call || msg;
          if (!c?.callControlId) return;
          setCalls((prev) => ({
            ...prev,
            [c.callControlId]: {
              callControlId: c.callControlId,
              campaignId: c.campaignId,
              campaignName: c.campaignName ?? null,
              leadId: c.leadId,
              leadName: c.leadName ?? null,
              leadPhone: c.leadPhone ?? null,
              startedAt: c.startedAt,
              status: c.status || 'connecting',
              turns: prev[c.callControlId]?.turns ?? [],
              lastText: c.lastText ?? null,
              engine: c.engine || msg.engine,
            },
          }));
          setSelectedId((prev) => prev || c.callControlId);
          return;
        }

        if (msg.type === 'transcript_snapshot' && msg.callControlId && Array.isArray(msg.turns)) {
          setCalls((prev) => {
            const existing = prev[msg.callControlId];
            const last = msg.turns[msg.turns.length - 1];
            return {
              ...prev,
              [msg.callControlId]: {
                callControlId: msg.callControlId,
                leadName: existing?.leadName ?? null,
                leadPhone: existing?.leadPhone ?? null,
                campaignName: existing?.campaignName ?? null,
                startedAt: existing?.startedAt ?? msg.at ?? new Date().toISOString(),
                endedAt: existing?.endedAt,
                status: existing?.status === 'ended' ? 'ended' : 'active',
                turns: msg.turns,
                lastText: last?.content ?? null,
                engine: existing?.engine,
              },
            };
          });
          setSelectedId((prev) => prev || msg.callControlId);
          return;
        }

        if (msg.type === 'turn' && msg.callControlId) {
          setCalls((prev) => {
            const existing = prev[msg.callControlId];
            // Don't drop early turns if call_started hasn't arrived yet.
            const base = existing ?? {
              callControlId: msg.callControlId,
              leadName: null,
              leadPhone: null,
              campaignName: null,
              startedAt: msg.at || new Date().toISOString(),
              status: 'active' as const,
              turns: [] as Array<{ role: string; content: string; at: string }>,
              lastText: null as string | null,
            };
            return {
              ...prev,
              [msg.callControlId]: {
                ...base,
                status: base.status === 'connecting' ? 'active' : base.status,
                turns: [...base.turns, { role: msg.role, content: msg.content, at: msg.at }],
                lastText: msg.content,
              },
            };
          });
          setSelectedId((prev) => prev || msg.callControlId);
          return;
        }

        if (msg.type === 'call_ended' && msg.callControlId) {
          setCalls((prev) => {
            const existing = prev[msg.callControlId];
            if (!existing) return prev;
            return {
              ...prev,
              [msg.callControlId]: {
                ...existing,
                status: 'ended',
                endReason: msg.reason,
                endedAt: new Date().toISOString(),
              },
            };
          });
        }
      };

      es.onerror = () => {
        setConnected(false);
        es.close();
        esRef.current = null;
        if (!closedRef.current) scheduleReconnect();
      };
    };

    void connect();

    return () => {
      closedRef.current = true;
      cleanup();
    };
  }, []);

  useEffect(() => {
    adminService
      .getTelnyxBotReadiness()
      .then(setReadiness)
      .catch(() => setReadiness(null));
  }, []);

  // Drop non-today ended cards (today's connects stay visible all day).
  useEffect(() => {
    const prune = () => {
      setCalls((prev) => {
        const entries = Object.entries(prev);
        const live = entries.filter(([, c]) => c.status !== 'ended');
        const endedKept = entries
          .filter(([, c]) => c.status === 'ended')
          .filter(([, c]) => {
            if (isSameLocalDay(c.endedAt) || isSameLocalDay(c.startedAt)) return true;
            if (!c.endedAt) return true;
            const ts = Date.parse(c.endedAt) || 0;
            return ts >= Date.now() - ENDED_CALL_RETENTION_MS;
          })
          .sort((a, b) =>
            (b[1].endedAt || b[1].startedAt || '').localeCompare(
              a[1].endedAt || a[1].startedAt || ''
            )
          )
          .slice(0, MAX_ENDED_CALLS);

        if (live.length + endedKept.length === entries.length) return prev;

        const next: Record<string, BotCall> = {};
        for (const [id, c] of live) next[id] = c;
        for (const [id, c] of endedKept) next[id] = c;
        return next;
      });
    };

    const id = window.setInterval(prune, 30_000);
    return () => window.clearInterval(id);
  }, []);

  // If the selected card was pruned, clear selection.
  useEffect(() => {
    if (selectedId && !calls[selectedId]) {
      setSelectedId(null);
    }
  }, [calls, selectedId]);

  const handleSyncPlatform = async () => {
    setSyncing(true);
    try {
      const result = await adminService.syncTelnyxPlatformAssistant();
      toast.success(`Platform assistant synced (webhook: ${result.webhookUrl})`);
      const next = await adminService.getTelnyxBotReadiness();
      setReadiness(next);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const callDurationLabel = (startedAt?: string, endedAt?: string) => {
    if (!startedAt) return '—';
    const start = new Date(startedAt).getTime();
    if (Number.isNaN(start)) return '—';
    const endRaw = endedAt ? new Date(endedAt).getTime() : Date.now();
    const end = Number.isNaN(endRaw) ? Date.now() : endRaw;
    const sec = Math.max(0, Math.floor((end - start) / 1000));
    return fmtDuration(sec);
  };

  const callList = useMemo(() => {
    const list = Object.values(calls);
    const rank = (s: string) => {
      if (s === 'connecting') return 0;
      if (s === 'active' || s === 'ending') return 1;
      return 2; // ended
    };
    return list.sort((a, b) => {
      const byStatus = rank(a.status) - rank(b.status);
      if (byStatus !== 0) return byStatus;
      return (b.startedAt || '').localeCompare(a.startedAt || '');
    });
  }, [calls]);

  const tabCounts = useMemo(() => {
    let connecting = 0;
    let active = 0;
    let ended = 0;
    let connected = 0;
    for (const c of callList) {
      if (c.status === 'connecting') connecting += 1;
      else if (c.status === 'active' || c.status === 'ending') active += 1;
      else if (c.status === 'ended') ended += 1;
      if (isHumanConnectedCall(c)) connected += 1;
    }
    return {
      all: callList.length,
      connecting,
      active,
      connected,
      ended,
    };
  }, [callList]);

  const filteredCalls = useMemo(
    () => callList.filter((c) => matchesCallStatusTab(c, statusTab)),
    [callList, statusTab]
  );

  const visibleCalls = useMemo(() => {
    if (statusTab === 'all') {
      const live = filteredCalls.filter((c) => c.status !== 'ended');
      const ended = filteredCalls.filter((c) => c.status === 'ended');
      // On All: never hide live calls behind pagination — only page ended history.
      const endedBudget = Math.max(0, Math.max(visibleLimit, live.length) - live.length);
      return [...live, ...ended.slice(0, endedBudget)];
    }
    return filteredCalls.slice(0, visibleLimit);
  }, [filteredCalls, statusTab, visibleLimit]);

  const hiddenCount = Math.max(0, filteredCalls.length - visibleCalls.length);
  const selected = selectedId ? calls[selectedId] : undefined;
  const activeCount = tabCounts.connecting + tabCounts.active;
  const endedCount = tabCounts.ended;

  const selectStatusTab = (tab: CallStatusTab) => {
    setStatusTab(tab);
    setVisibleLimit(CALL_LIST_PAGE_SIZE);
  };

  const campaignPacing = analytics?.campaignPacing ?? [];
  const agents = analytics?.agents ?? [];
  const dialWindow = analytics?.dialWindow;
  const leadCounts = analytics?.leadStatusCounts ?? {};
  const breakdownStats = analytics?.breakdownStats;

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <CpuChipIcon className="h-7 w-7 text-primary" />
          Bot Monitor
          <span className="text-sm font-normal text-muted-foreground">(AI calling)</span>
        </h1>
        <div className="flex items-center gap-2 text-sm">
          <span
            className={`h-2.5 w-2.5 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}
          />
          <span className="text-muted-foreground">
            {connected ? `Live · ${activeCount} active` : 'Reconnecting…'}
          </span>
        </div>
      </div>

      {readiness && (
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-foreground mb-2">Telnyx Bot Setup</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  {(
                    [
                      ['Telnyx API key', readiness.hasApiKey],
                      ['Platform assistant ID', readiness.hasPlatformAssistantId],
                      ['PUBLIC_BASE_URL', readiness.hasPublicBaseUrl],
                      ['Webhook public key', readiness.hasWebhookPublicKey],
                      ['LLM API key ref', readiness.hasLlmApiKeyRef],
                      ['Insight group ID', readiness.hasInsightGroupId],
                    ] as const
                  ).map(([label, ok]) => (
                    <div key={label} className="flex items-center gap-1.5 text-muted-foreground">
                      {ok ? (
                        <CheckCircleIcon className="h-4 w-4 text-green-500 shrink-0" />
                      ) : (
                        <XCircleIcon className="h-4 w-4 text-amber-500 shrink-0" />
                      )}
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                {readiness.assistantVariablesWebhookUrl && (
                  <p className="mt-2 text-xs text-muted-foreground break-all">
                    Variables webhook: {readiness.assistantVariablesWebhookUrl}
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={syncing || !readiness.hasPlatformAssistantId}
                onClick={handleSyncPlatform}
              >
                {syncing ? 'Syncing…' : 'Sync platform assistant'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatCard label="Active now" value={String(analytics.activeNow)} />
          {(analytics.interruptions?.opened ?? 0) > 0 && (
            <StatCard
              label="Interruptions (today)"
              value={String(analytics.interruptions!.opened)}
              hint={
                `${analytics.interruptions!.resolvedByAnswer} answered / ${
                  analytics.interruptions!.forceResolved
                } timed out${
                  analytics.interruptions!.forceResolvedRate >= 0.4
                    ? ' — detection may be firing too often'
                    : ''
                }`
              }
            />
          )}
          <StatCard label="Calls (24h)" value={String(analytics.last24h.calls)} />
          <StatCard label="Calls (today)" value={String(analytics.last7d.calls)} />
          <StatCard label="Avg duration (today)" value={fmtDuration(analytics.last7d.avgDurationSec)} />
          <StatCard
            label="Cost (today)"
            value={`$${(analytics.last7d.totalCostUsd ?? analytics.last7d.estimatedCostUsd).toFixed(2)}`}
          />
          <StatCard
            label="Avg $/min (today)"
            value={
              analytics.last7d.avgCostPerMinuteUsd && analytics.last7d.avgCostPerMinuteUsd > 0
                ? `$${analytics.last7d.avgCostPerMinuteUsd.toFixed(3)}`
                : '—'
            }
          />
        </div>
      )}

      {analytics && (analytics.last7d.actualCostUsd ?? 0) > 0 && (
        <p className="text-xs text-muted-foreground -mt-3">
          ${analytics.last7d.actualCostUsd!.toFixed(2)} tracked from VAPI/Telnyx billing
          {(analytics.last7d.estimatedCostUsd ?? 0) > 0
            ? ` · $${analytics.last7d.estimatedCostUsd.toFixed(2)} estimated for ${analytics.last7d.calls - (analytics.last7d.chargedCalls ?? 0)} calls without cost data`
            : ''}
        </p>
      )}

      {campaignPacing.length > 0 && (
        <Card className="card-elevated">
          <div className="px-5 py-4 border-b border-border bg-muted/50">
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              <BoltIcon className="h-5 w-5 text-primary" />
              Campaign pacing
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Active campaigns only ({campaignPacing.length})
            </p>
          </div>
          <CardContent className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {campaignPacing.map((c) => (
                <div key={c.id} className="rounded-lg border border-border/70 p-4 bg-muted/20">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="font-semibold text-sm truncate">{c.name}</p>
                    <div className="flex items-center gap-1 shrink-0">
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          PACING_HEALTH_STYLES[c.pacing?.health ?? 'normal'].className
                        }`}
                      >
                        {PACING_HEALTH_STYLES[c.pacing?.health ?? 'normal'].label}
                      </span>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                      <span>Pipeline</span>
                      <span>{c.pacing?.fillPercent ?? 0}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${Math.min(100, c.pacing?.fillPercent ?? 0)}%` }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <span className="text-muted-foreground">Dial ratio</span>
                    <span className="font-medium text-right">{c.pacing?.dialRatio ?? c.dialRatio}</span>
                    <span className="text-muted-foreground">PC rate</span>
                    <span
                      className={`font-medium text-right ${pcRateColor(
                        (c.pacing?.predictiveCancelledRate ?? 0) * 100
                      )}`}
                    >
                      {Math.round((c.pacing?.predictiveCancelledRate ?? 0) * 1000) / 10}%
                    </span>
                    <span className="text-muted-foreground">Available agents</span>
                    <span className="font-medium text-right">{c.pacing?.availableAgents ?? 0}</span>
                    <span className="text-muted-foreground">Active / target</span>
                    <span className="font-medium text-right">
                      {c.pacing?.activeCalls ?? 0} / {c.pacing?.targetActiveCalls ?? 0}
                    </span>
                    <span className="text-muted-foreground">Deficit</span>
                    <span
                      className={`font-medium text-right ${
                        (c.pacing?.deficit ?? 0) > 0 ? 'text-blue-600 dark:text-blue-400' : ''
                      }`}
                    >
                      {c.pacing?.deficit ?? 0}
                    </span>
                  </div>
                  {c.capacity && (
                    <div className="mt-3 pt-3 border-t border-border/60">
                      <p className="text-[11px] font-semibold text-primary uppercase tracking-wide mb-2">
                        Capacity dialer
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <span className="text-muted-foreground">Active AI / target</span>
                        <span className="font-medium text-right">
                          {c.capacity.activeAiConversations} / {c.capacity.targetActiveAi}
                        </span>
                        <span className="text-muted-foreground">Pipeline</span>
                        <span className="font-medium text-right">
                          {c.capacity.pipelineCalls} / {c.capacity.pipelineCap}
                        </span>
                        <span className="text-muted-foreground">Pipeline room</span>
                        <span className="font-medium text-right">{c.capacity.pipelineRoom}</span>
                        <span className="text-muted-foreground">Answer rate</span>
                        <span className="font-medium text-right">
                          {Math.round(c.capacity.answerRate * 1000) / 10}%
                        </span>
                        <span className="text-muted-foreground">Next wave</span>
                        <span className="font-medium text-right">{c.capacity.recommendedWaveSize}</span>
                        <span className="text-muted-foreground">Avg turn latency</span>
                        <span
                          className={`font-medium text-right ${
                            c.capacity.latencyElevated
                              ? 'text-amber-700 dark:text-amber-400'
                              : ''
                          }`}
                        >
                          {c.capacity.avgTurnLatencyMs > 0
                            ? `${(c.capacity.avgTurnLatencyMs / 1000).toFixed(1)}s`
                            : '—'}
                        </span>
                      </div>
                      {c.capacity.latencyElevated && (
                        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                          Wave paused — turn latency above threshold
                        </p>
                      )}
                    </div>
                  )}
                  {c.pacing?.isPaused && (
                    <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                      Emergency pause until{' '}
                      {c.pacing.pausedUntil
                        ? new Date(c.pacing.pausedUntil).toLocaleTimeString()
                        : '—'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {dialWindow && (
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium">Dial timeline / timezone</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Now ET: {dialWindow.nowEt}
                  {dialWindow.timezoneSource !== 'none'
                    ? ` · source ${dialWindow.timezoneSource.replace(/_/g, ' ')}`
                    : ''}
                </div>
              </div>
              <span
                className={`inline-flex text-xs font-medium px-2 py-1 rounded-full ${
                  dialWindow.idleReason === 'dialing' || dialWindow.idleReason === 'wave_disabled'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                    : dialWindow.idleReason === 'at_target'
                      ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                }`}
              >
                {dialWindow.idleReasonLabel}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Active zones</div>
                <div className="font-medium">
                  {dialWindow.activeZones.length
                    ? dialWindow.activeZones.join(', ')
                    : dialWindow.etWaveZones == null
                      ? 'None (outside wave)'
                      : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">ET wave</div>
                <div className="font-medium">{dialWindow.etWaveZones?.join(', ') ?? 'Closed'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Leads in window</div>
                <div className="text-xl font-bold">{dialWindow.eligibleLeadsInWindow}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Eligible total</div>
                <div className="text-xl font-bold">{dialWindow.eligibleLeadsTotal}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {agents.length > 0 && (
        <Card className="card-elevated">
          <div className="px-5 py-4 border-b border-border bg-muted/50">
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5 text-primary" />
              Agents ({agents.length})
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {agents.filter((a) => a.callStatus === 'in-call').length} on call &middot;{' '}
              {agents.filter((a) => a.callStatus === 'ringing').length} dialing &middot;{' '}
              {agents.filter((a) => a.callStatus === 'idle').length} idle
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground/80">Calling lights:</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden />
                Green — Connected (live with customer)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden />
                Yellow — Dialing / ringing (attempt in progress)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-muted-foreground/20" aria-hidden />
                Grey — Available line
              </span>
            </div>
          </div>
          <CardContent className="p-5">
            <div className="overflow-x-auto rounded-xl border border-border/70">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 border-b border-border/60">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-foreground">Agent</th>
                    <th
                      className="px-3 py-3 text-left font-semibold text-foreground min-w-[10.5rem]"
                      title="Idle time since last dispositioned connect."
                    >
                      Waiting now
                    </th>
                    <th
                      className="px-3 py-3 text-center font-semibold text-blue-700 dark:text-blue-300"
                      title="Green = connected · Yellow = dialing/ringing · Grey = available"
                    >
                      Calling
                    </th>
                    <th className="px-3 py-3 text-center font-semibold text-emerald-700 dark:text-emerald-300">
                      Connected
                    </th>
                    <th className="px-3 py-3 text-center font-semibold text-violet-700 dark:text-violet-300">
                      Interested
                    </th>
                    <th className="px-3 py-3 text-center font-semibold text-amber-700 dark:text-amber-300">
                      Follow Up
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => (
                    <tr
                      key={agent.id}
                      className="border-b border-border/50 last:border-b-0 hover:bg-accent/20 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="h-10 w-10 rounded-full bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center text-sm font-semibold text-primary">
                              {(agent.aiName || 'A').charAt(0).toUpperCase()}
                            </div>
                            <span
                              className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-background ${
                                agent.callStatus === 'in-call'
                                  ? 'bg-green-500 animate-pulse'
                                  : agent.callStatus === 'ringing'
                                    ? 'bg-yellow-500 animate-pulse'
                                    : 'bg-muted-foreground/40'
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm md:text-[15px] font-semibold text-foreground truncate">
                              {agent.aiName}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">{agent.campaignName}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-middle">
                        <div className="flex flex-col gap-1 min-w-[9.5rem]">
                          {agent.callStatus === 'idle' && (agent.currentIdleWaitSeconds ?? 0) > 0 ? (
                            <span
                              className={`text-sm font-semibold tabular-nums leading-none ${
                                (agent.currentIdleWaitSeconds ?? 0) >= 120
                                  ? 'text-red-600 dark:text-red-400'
                                  : (agent.currentIdleWaitSeconds ?? 0) >= 60
                                    ? 'text-amber-700 dark:text-amber-400'
                                    : 'text-foreground'
                              }`}
                            >
                              {formatWaitTime(agent.currentIdleWaitSeconds ?? 0)}
                            </span>
                          ) : agent.callStatus !== 'idle' ? (
                            <span className="text-sm font-semibold tabular-nums text-muted-foreground leading-none">
                              —
                            </span>
                          ) : null}
                          <div className="grid grid-cols-[5rem_1fr] gap-x-2 gap-y-0.5 text-[11px] leading-snug">
                            <span className="text-muted-foreground">Last Contact</span>
                            <span
                              className="tabular-nums text-foreground/85 whitespace-nowrap"
                              title="Time since last connected disposition"
                            >
                              {(agent.secondsSinceLastContact ?? 0) > 0
                                ? `${formatWaitTime(agent.secondsSinceLastContact)} ago`
                                : '—'}
                            </span>
                            {agent.avgWaitBetweenCallsSeconds > 0 ? (
                              <>
                                <span className="text-muted-foreground">Avg wait</span>
                                <span
                                  className="tabular-nums text-muted-foreground whitespace-nowrap"
                                  title="Average time between connected dispositions today"
                                >
                                  {formatWaitTime(agent.avgWaitBetweenCallsSeconds)}
                                </span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="inline-flex flex-col items-center gap-1">
                          <span className="inline-flex min-w-9 justify-center rounded-md border border-blue-200/70 dark:border-blue-800/60 bg-blue-50/80 dark:bg-blue-900/20 px-2 py-0.5 font-semibold text-blue-700 dark:text-blue-300">
                            {agent.activeCalls}
                          </span>
                          <div className="flex gap-1">
                            {Array.from({ length: agent.maxCalls }).map((_, i) => (
                              <span
                                key={i}
                                className={`h-2 w-2 rounded-full ${
                                  i < agent.bridgedCalls
                                    ? 'bg-green-500'
                                    : i < agent.activeCalls
                                      ? 'bg-amber-400'
                                      : 'bg-muted-foreground/20'
                                }`}
                                title={
                                  i < agent.bridgedCalls
                                    ? `Line ${i + 1}: Connected (live with customer)`
                                    : i < agent.activeCalls
                                      ? `Line ${i + 1}: Dialing / ringing`
                                      : `Line ${i + 1}: Available`
                                }
                              />
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex min-w-9 justify-center rounded-md border border-emerald-200/70 dark:border-emerald-800/60 bg-emerald-50/80 dark:bg-emerald-900/20 px-2 py-0.5 font-semibold text-emerald-700 dark:text-emerald-300">
                          {agent.dailyCounters.answeredCalls}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex min-w-9 justify-center rounded-md border border-violet-200/70 dark:border-violet-800/60 bg-violet-50/80 dark:bg-violet-900/20 px-2 py-0.5 font-semibold text-violet-700 dark:text-violet-300">
                          {agent.dailyCounters.interested}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex min-w-9 justify-center rounded-md border border-amber-200/70 dark:border-amber-800/60 bg-amber-50/80 dark:bg-amber-900/20 px-2 py-0.5 font-semibold text-amber-700 dark:text-amber-300">
                          {agent.dailyCounters.followUps}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lead Status Breakdown (same layout as Auto Dial / Live Monitor) */}
      <Card className="card-elevated">
        <div className="px-5 py-4 border-b border-border bg-muted/50">
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-primary" />
            Lead Status Breakdown
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">Outcomes for today (AI bot campaigns)</p>
        </div>
        <CardContent className="p-5">
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
              <div className="text-xl font-bold text-foreground leading-tight">
                {breakdownStats?.totalDialedToday ?? 0}
              </div>
              <div className="text-xs font-medium text-muted-foreground mt-1">Total Calls</div>
              <div className="text-[11px] text-muted-foreground/80">
                {breakdownStats?.connectedToday ?? 0} dispositioned
              </div>
            </div>
            <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
              <div className="text-xl font-bold text-foreground leading-tight">
                {(breakdownStats?.connectedToday ?? 0) > 0
                  ? `1:${Math.max(
                      Math.round(
                        (breakdownStats?.totalDialedToday ?? 0) /
                          (breakdownStats?.connectedToday ?? 1)
                      ),
                      1
                    )}`
                  : '1:0'}
              </div>
              <div className="text-xs font-medium text-muted-foreground mt-1">Call-to-Answer Ratio</div>
              <div className="text-[11px] text-muted-foreground/80">connections per calls</div>
            </div>
            <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
              <div className="text-xl font-bold text-foreground leading-tight">
                {formatWaitTime(breakdownStats?.avgWaitToNextCallSeconds ?? 0)}
              </div>
              <div className="text-xs font-medium text-muted-foreground mt-1">Avg gap between calls</div>
              <div className="text-[11px] text-muted-foreground/80">day average · not live idle</div>
            </div>
            <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
              <div className="text-xl font-bold text-foreground leading-tight">
                {formatSeconds(breakdownStats?.connectedAvgDuration ?? 0)}
              </div>
              <div className="text-xs font-medium text-muted-foreground mt-1">
                Avg Connected Call Duration
              </div>
              <div className="text-[11px] text-muted-foreground/80">dispositioned calls only</div>
            </div>
          </div>
          <div className="space-y-3">
            {Object.entries(LEAD_STATUS_LABELS).map(([key, meta]) => {
              const count = leadCounts[key] ?? 0;
              const total = Math.max(Number(leadCounts.total || 0), 1);
              const pct = Math.round((count / total) * 100);
              return (
                <div key={key} className="flex items-center gap-3">
                  <div className="w-28 flex-shrink-0">
                    <span
                      className={`inline-flex text-xs px-2 py-0.5 rounded-full font-medium ${meta.color}`}
                    >
                      {meta.label}
                    </span>
                  </div>
                  <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${meta.color.split(' ')[0]}`}
                      style={{ width: `${Math.max(pct, count > 0 ? 1 : 0)}%` }}
                    />
                  </div>
                  <div className="w-16 text-right">
                    <span className="text-sm font-semibold text-foreground">{count}</span>
                    <span className="text-xs text-muted-foreground ml-1">({pct}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>


      <div className="space-y-3">
        {/* Full-width tab row so 5 tabs stay on one line (not wrapping Ended below). */}
        <div
          className="flex flex-nowrap items-stretch gap-1 p-1 rounded-lg border border-border bg-muted/30 overflow-x-auto"
          role="tablist"
          aria-label="Call status"
        >
          {CALL_STATUS_TABS.map((tab) => {
            const count = tabCounts[tab.id];
            const selectedTab = statusTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selectedTab}
                onClick={() => selectStatusTab(tab.id)}
                className={`flex-1 min-w-0 whitespace-nowrap rounded-md px-2.5 py-2 text-xs font-medium transition-colors ${
                  selectedTab
                    ? 'bg-background text-foreground shadow-sm border border-border'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                }`}
              >
                {tab.label}
                <span className="ml-1 tabular-nums opacity-70">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-2 px-0.5">
          <p className="text-xs text-muted-foreground">
            {filteredCalls.length === 0
              ? `No ${statusTab === 'all' ? '' : statusTab + ' '}calls`
              : `Showing ${visibleCalls.length} of ${filteredCalls.length}`}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Call list */}
          <div className="lg:col-span-1 flex flex-col min-h-0">
            <div className="space-y-2 max-h-[min(70vh,720px)] overflow-y-auto pr-1">
              {visibleCalls.length === 0 ? (
                <Card className="card-elevated">
                  <CardContent className="p-6 text-center text-sm text-muted-foreground">
                    {callList.length === 0
                      ? 'No AI bot calls right now. Live calls will appear here as they start.'
                      : statusTab === 'connected'
                        ? 'No human-connected calls yet (need actual talk on the transcript).'
                        : `No ${statusTab} calls in this view.`}
                  </CardContent>
                </Card>
              ) : (
                visibleCalls.map((c) => (
                  <button
                    key={c.callControlId}
                    onClick={() => setSelectedId(c.callControlId)}
                    className={`w-full text-left rounded-lg border p-3 transition-colors ${
                      selectedId === c.callControlId
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-sm font-medium text-foreground truncate">
                        <PhoneIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                        {c.leadName || (c.leadId ? `Lead ${String(c.leadId).slice(0, 8)}` : 'Unknown lead')}
                      </span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {isHumanConnectedCall(c) && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                            Human
                          </span>
                        )}
                        {c.engine && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${engineBadgeClass(c.engine)}`}>
                            {engineLabel(c.engine)}
                          </span>
                        )}
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            statusStyles[c.status] || statusStyles.active
                          }`}
                        >
                          {c.status}
                        </span>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      {c.leadPhone && <span>{c.leadPhone}</span>}
                      {c.campaignName && <span className="truncate">{c.campaignName}</span>}
                      <span>{callDurationLabel(c.startedAt, c.endedAt)}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground truncate">
                      {c.status === 'ended' && c.endReason
                        ? c.endReason
                        : c.lastText || 'Waiting for conversation…'}
                    </div>
                  </button>
                ))
              )}
            </div>
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setVisibleLimit((n) => n + CALL_LIST_PAGE_SIZE)}
                className="mt-2 w-full rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/70 transition-colors"
              >
                Load more ({hiddenCount} remaining)
              </button>
            )}
          </div>

          {/* Transcript */}
          <Card className="card-elevated lg:col-span-2">
            <CardContent className="p-4">
              {!selected ? (
                <div className="text-muted-foreground text-sm">Select a call to view its transcript.</div>
              ) : (
                <>
                  <div className="flex items-center justify-between border-b border-border pb-2 mb-3 gap-2">
                    <div className="text-sm text-foreground min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">
                          {selected.leadName || 'Live transcript'}
                        </span>
                        {selected.engine && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${engineBadgeClass(selected.engine)}`}>
                            {engineLabel(selected.engine)}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {[selected.leadPhone, selected.campaignName || (selected.campaignId ? `campaign ${String(selected.campaignId).slice(0, 8)}` : null), callDurationLabel(selected.startedAt, selected.endedAt)]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        statusStyles[selected.status] || statusStyles.active
                      }`}
                    >
                      {selected.status}
                      {selected.endReason ? ` · ${selected.endReason}` : ''}
                    </span>
                  </div>

                  <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    {selected.turns.length === 0 ? (
                      <div className="text-sm text-muted-foreground">
                        {selected.status === 'ended'
                          ? 'No full transcript saved for this call.'
                          : 'Live transcript will appear here as the conversation progresses.'}
                        {selected.lastText ? (
                          <div className="mt-2 italic">Latest: “{selected.lastText}”</div>
                        ) : null}
                      </div>
                    ) : (
                      selected.turns.map((t, i) => {
                        const isAI = t.role === 'assistant';
                        return (
                          <div
                            key={i}
                            className={`flex gap-2 ${isAI ? '' : 'flex-row-reverse'}`}
                          >
                            <div
                              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                                isAI ? 'bg-primary/10 text-primary' : 'bg-muted text-foreground'
                              }`}
                            >
                              {isAI ? (
                                <CpuChipIcon className="h-4 w-4" />
                              ) : (
                                <UserIcon className="h-4 w-4" />
                              )}
                            </div>
                            <div
                              className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${
                                isAI
                                  ? 'bg-primary/10 text-foreground rounded-tl-sm'
                                  : 'bg-muted text-foreground rounded-tr-sm'
                              }`}
                            >
                              {t.content}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="card-elevated">
        <CardContent className="p-4">
          <h2 className="text-sm font-semibold mb-3">Activity log</h2>
          <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
            {activityLog.length === 0 ? (
              <p className="text-xs text-muted-foreground">Waiting for dial events…</p>
            ) : (
              activityLog.map((entry) => (
                <div key={entry.id} className="text-xs border-b border-border/50 pb-2">
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-mono">
                    <span>{new Date(entry.at).toLocaleTimeString()}</span>
                    {entry.type && (
                      <span className="rounded bg-muted px-1.5 py-0.5 font-sans text-[9px] uppercase tracking-wide">
                        {BOT_ACTIVITY_TYPE_LABELS[entry.type] || entry.type}
                      </span>
                    )}
                  </div>
                  <div className={`font-medium ${BOT_ACTIVITY_LEVEL_STYLES[entry.level]}`}>
                    {entry.message}
                  </div>
                  {(entry.leadName || entry.leadPhone || entry.campaignName) && (
                    <div className="text-muted-foreground mt-0.5">
                      {[entry.leadName || entry.leadPhone, entry.campaignName]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <LiveCallsSection botOnly hideBotMonitorLink />
    </div>
  );
};

export default BotMonitorPage;
